import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Key,
  Settings,
  Brain,
  Shield,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Save,
  Calendar,
  RefreshCw,
  Loader2,
  ExternalLink,
  Server
} from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

const LLM_PROVIDERS = [
  { value: 'anthropic', label: 'Anthropic (Claude)', description: 'Claude models for assessments' },
  { value: 'openai', label: 'OpenAI', description: 'GPT models' },
  { value: 'google', label: 'Google', description: 'Gemini models' },
];

const API_KEY_PROVIDERS = [
  { value: 'anthropic', label: 'Anthropic API Key', description: 'For Claude AI assessments' },
  { value: 'resend', label: 'Resend API Key', description: 'For email delivery' },
  { value: 'markupgo', label: 'MarkupGo API Key', description: 'For PDF generation' },
  { value: 'openai', label: 'OpenAI API Key', description: 'For GPT models (optional)' },
  { value: 'google', label: 'Google API Key', description: 'For Gemini models (optional)' },
];

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState([]);
  const [envKeys, setEnvKeys] = useState({});
  const [settings, setSettings] = useState({});
  const [showAddKey, setShowAddKey] = useState(false);
  const [newKeyProvider, setNewKeyProvider] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [showKeyValue, setShowKeyValue] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // LLM Settings state
  const [defaultLLMProvider, setDefaultLLMProvider] = useState('anthropic');
  const [defaultModel, setDefaultModel] = useState('claude-opus-4-5-20251101');
  const [availableModels, setAvailableModels] = useState({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // Lunacal Settings state
  const [lunacalSettings, setLunacalSettings] = useState({
    enabled: true,
    calLink: 'team/secretsauce/dicover-path',
    bookingUrl: 'https://book.secretsauce.fyi/team/secretsauce/dicover-path',
    embedType: 'inline',
    sessionName: 'Discover PATH',
    sessionDuration: 30,
    forwardQueryParams: true
  });
  const [savingLunacal, setSavingLunacal] = useState(false);

  // Email Config state
  const [emailConfig, setEmailConfig] = useState({
    fromEmail: '', fromName: '', replyToEmail: ''
  });
  const [savingEmail, setSavingEmail] = useState(false);

  // PDF Config state
  const [pdfConfig, setPdfConfig] = useState({
    markupgoApiUrl: '', defaultMarginTop: '10mm', defaultMarginBottom: '10mm',
    defaultMarginLeft: '10mm', defaultMarginRight: '10mm', defaultOrientation: 'portrait'
  });
  const [pdfKeyInfo, setPdfKeyInfo] = useState(null);
  const [resendKeyInfo, setResendKeyInfo] = useState(null);
  const [savingPdf, setSavingPdf] = useState(false);
  const [testingMarkupGo, setTestingMarkupGo] = useState(false);
  const [markupGoStatus, setMarkupGoStatus] = useState(null);

  useEffect(() => {
    fetchData();
    fetchModels();
  }, []);

  useEffect(() => {
    // Re-fetch models when provider changes
    if (defaultLLMProvider && availableModels[defaultLLMProvider]) {
      // Set first model as default if current model doesn't belong to provider
      const providerModels = availableModels[defaultLLMProvider];
      if (providerModels && providerModels.length > 0) {
        const modelExists = providerModels.some(m => m.id === defaultModel);
        if (!modelExists) {
          setDefaultModel(providerModels[0].id);
        }
      }
    }
  }, [defaultLLMProvider, availableModels]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [keysRes, settingsRes, envRes, tidyCalRes, emailRes, pdfRes] = await Promise.all([
        axios.get('/api/admin/api-keys'),
        axios.get('/api/admin/settings'),
        axios.get('/api/admin/api-keys/env-status'),
        axios.get('/api/admin/integrations/lunacal'),
        axios.get('/api/admin/config/email').catch(() => ({ data: { config: {} } })),
        axios.get('/api/admin/config/pdf').catch(() => ({ data: { config: {} } }))
      ]);
      
      setApiKeys(keysRes.data.apiKeys || []);
      setEnvKeys(envRes.data.envKeys || {});
      
      // Parse settings
      const settingsMap = {};
      (settingsRes.data.settings || []).forEach(s => {
        settingsMap[s.key] = s.value;
      });
      setSettings(settingsMap);
      
      if (settingsMap.defaultLLMProvider) {
        setDefaultLLMProvider(settingsMap.defaultLLMProvider);
      }
      if (settingsMap.defaultModel) {
        setDefaultModel(settingsMap.defaultModel);
      }
      
      if (tidyCalRes.data.lunacal) {
        setLunacalSettings(tidyCalRes.data.lunacal);
      }

      // Parse email config
      const ec = emailRes.data.config || {};
      setEmailConfig({
        fromEmail: ec.fromEmail?.value || '',
        fromName: ec.fromName?.value || '',
        replyToEmail: ec.replyToEmail?.value || ''
      });
      setResendKeyInfo(ec.resendApiKey || null);

      // Parse PDF config
      const pc = pdfRes.data.config || {};
      setPdfConfig({
        markupgoApiUrl: pc.markupgoApiUrl?.value || '',
        defaultMarginTop: pc.defaultMarginTop?.value || '10mm',
        defaultMarginBottom: pc.defaultMarginBottom?.value || '10mm',
        defaultMarginLeft: pc.defaultMarginLeft?.value || '10mm',
        defaultMarginRight: pc.defaultMarginRight?.value || '10mm',
        defaultOrientation: pc.defaultOrientation?.value || 'portrait'
      });
      setPdfKeyInfo(pc.markupgoApiKey || null);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      setLoadingModels(true);
      const res = await axios.get('/api/admin/llm/models');
      setAvailableModels(res.data.models || {});
    } catch (error) {
      console.error('Failed to fetch models:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleAddApiKey = async () => {
    if (!newKeyProvider || !newKeyValue) {
      toast.error('Please select a provider and enter the API key');
      return;
    }
    
    try {
      setSaving(true);
      await axios.post('/api/admin/api-keys', {
        provider: newKeyProvider,
        apiKey: newKeyValue
      });
      toast.success(`API key for ${newKeyProvider} saved successfully`);
      setShowAddKey(false);
      setNewKeyProvider('');
      setNewKeyValue('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApiKey = async (provider) => {
    if (!window.confirm(`Delete API key for ${provider}?`)) return;
    
    try {
      await axios.delete(`/api/admin/api-keys/${provider}`);
      toast.success(`API key for ${provider} deleted`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const handleSaveLLMSettings = async () => {
    try {
      setSaving(true);
      await Promise.all([
        axios.put('/api/admin/settings/defaultLLMProvider', { value: defaultLLMProvider }),
        axios.put('/api/admin/settings/defaultModel', { value: defaultModel })
      ]);
      toast.success('LLM settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setConnectionStatus(null);
      const res = await axios.get(`/api/admin/llm/test-connection?provider=${defaultLLMProvider}&model=${defaultModel}`);
      setConnectionStatus(res.data);
      if (res.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(res.data.error || 'Connection failed');
      }
    } catch (error) {
      setConnectionStatus({ success: false, error: error.message });
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveLunacalSettings = async () => {
    try {
      setSavingLunacal(true);
      await axios.put('/api/admin/integrations/lunacal', lunacalSettings);
      toast.success('Lunacal settings saved');
    } catch (error) {
      toast.error('Failed to save Lunacal settings');
    } finally {
      setSavingLunacal(false);
    }
  };

  const handleSaveEmailConfig = async () => {
    try {
      setSavingEmail(true);
      await axios.put('/api/admin/config/email', emailConfig);
      toast.success('Email configuration saved');
    } catch (error) {
      toast.error('Failed to save email configuration');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSavePdfConfig = async () => {
    try {
      setSavingPdf(true);
      await axios.put('/api/admin/config/pdf', pdfConfig);
      toast.success('PDF configuration saved');
    } catch (error) {
      toast.error('Failed to save PDF configuration');
    } finally {
      setSavingPdf(false);
    }
  };

  const handleTestMarkupGo = async () => {
    try {
      setTestingMarkupGo(true);
      const res = await axios.post('/api/admin/config/pdf/test');
      setMarkupGoStatus(res.data);
    } catch (error) {
      setMarkupGoStatus({ success: false, error: 'Connection test failed' });
    } finally {
      setTestingMarkupGo(false);
    }
  };

  const isKeyConfigured = (provider) => {
    return apiKeys.some(k => k.provider === provider && k.isConfigured);
  };

  const getKeySource = (provider) => {
    const envKey = envKeys[provider];
    const dbKey = isKeyConfigured(provider);
    
    if (envKey?.isSet) return 'env';
    if (dbKey) return 'database';
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="admin-settings-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage API keys, integrations, and system configuration</p>
      </div>

      <Tabs defaultValue="api-keys" className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="api-keys" className="flex items-center gap-2">
            <Key className="w-4 h-4" /> API Keys
          </TabsTrigger>
          <TabsTrigger value="llm" className="flex items-center gap-2">
            <Brain className="w-4 h-4" /> LLM Config
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Server className="w-4 h-4" /> Email
          </TabsTrigger>
          <TabsTrigger value="pdf" className="flex items-center gap-2">
            <Shield className="w-4 h-4" /> PDF
          </TabsTrigger>
          <TabsTrigger value="lunacal" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Lunacal
          </TabsTrigger>
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="w-4 h-4" /> General
          </TabsTrigger>
        </TabsList>

        {/* API Keys Tab */}
        <TabsContent value="api-keys">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" /> API Key Management
              </CardTitle>
              <CardDescription>
                Manage API keys for external services. Keys can be set via environment variables or stored encrypted in the database.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {API_KEY_PROVIDERS.map((provider) => {
                  const dbConfigured = isKeyConfigured(provider.value);
                  const envKey = envKeys[provider.value];
                  const keyInfo = apiKeys.find(k => k.provider === provider.value);
                  const source = getKeySource(provider.value);
                  
                  return (
                    <div 
                      key={provider.value}
                      className="flex items-center justify-between p-4 border border-border rounded-lg"
                      data-testid={`api-key-${provider.value}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${source ? 'bg-green-100' : 'bg-muted'}`}>
                          {source ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{provider.label}</p>
                          <p className="text-sm text-muted-foreground">{provider.description}</p>
                          {keyInfo?.lastUpdated && (
                            <p className="text-xs text-muted-foreground">
                              Last updated: {formatDate(keyInfo.lastUpdated)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {source === 'env' && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Server className="w-3 h-3 mr-1" />
                            Using ENV: {envKey?.preview}
                          </Badge>
                        )}
                        {source === 'database' && (
                          <Badge variant="default">
                            Configured (Database)
                          </Badge>
                        )}
                        {!source && (
                          <Badge variant="secondary">
                            Not Set
                          </Badge>
                        )}
                        {dbConfigured && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleDeleteApiKey(provider.value)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setNewKeyProvider(provider.value);
                            setShowAddKey(true);
                          }}
                        >
                          {dbConfigured ? 'Update' : 'Add'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Security Notice */}
              <div className="mt-6 p-4 border border-amber-200 bg-amber-50/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Security Notice</p>
                    <p className="text-sm text-amber-700 mt-1">
                      API keys set via environment variables take priority over database-stored keys. 
                      Database keys are encrypted before storage and are never displayed in plain text.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LLM Configuration Tab */}
        <TabsContent value="llm">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5" /> LLM Configuration
              </CardTitle>
              <CardDescription>
                Configure the default AI provider and model for assessments.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <div className="space-y-2">
                  <Label htmlFor="llm-provider">AI Provider</Label>
                  <Select value={defaultLLMProvider} onValueChange={setDefaultLLMProvider}>
                    <SelectTrigger id="llm-provider" data-testid="llm-provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LLM_PROVIDERS.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          <div className="flex items-center gap-2">
                            {p.label}
                            {getKeySource(p.value) && (
                              <CheckCircle className="w-3 h-3 text-green-500" />
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {LLM_PROVIDERS.find(p => p.value === defaultLLMProvider)?.description}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="llm-model">Model {loadingModels && <Loader2 className="inline w-3 h-3 animate-spin text-muted-foreground ml-1" />}</Label>
                  <Select value={defaultModel} onValueChange={setDefaultModel}>
                    <SelectTrigger id="llm-model" data-testid="llm-model-select">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {(availableModels[defaultLLMProvider] || []).map(model => (
                        <SelectItem key={model.id} value={model.id}>
                          <div>
                            <span className="font-medium">{model.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ({Math.round(model.contextWindow / 1000)}K context)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {availableModels[defaultLLMProvider]?.find(m => m.id === defaultModel)?.description || '\u00A0'}
                  </p>
                </div>
              </div>

              {/* Connection Status */}
              {connectionStatus && (
                <div className={`p-4 rounded-lg ${connectionStatus.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {connectionStatus.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={connectionStatus.success ? 'text-green-700' : 'text-red-700'}>
                      {connectionStatus.success ? 'Connection successful' : connectionStatus.error}
                    </span>
                    {connectionStatus.source && (
                      <Badge variant="outline" className="ml-2">
                        Using: {connectionStatus.source === 'env' ? 'Environment Variable' : 'Database Key'}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-4">
                <Button onClick={handleSaveLLMSettings} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save LLM Settings'}
                </Button>
                <Button variant="outline" onClick={handleTestConnection} disabled={testingConnection}>
                  {testingConnection ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Email Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5" /> Email Configuration (Resend)
              </CardTitle>
              <CardDescription>
                Configure email sending settings. Values saved here override environment variables.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Resend API Key Status */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">Resend API Key</p>
                  <p className="text-sm text-muted-foreground">Required for sending emails</p>
                </div>
                <div className="flex items-center gap-2">
                  {resendKeyInfo?.isSet ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <CheckCircle className="w-3 h-3 mr-1" /> ENV: {resendKeyInfo.preview}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      <AlertCircle className="w-3 h-3 mr-1" /> Not Set
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <div className="space-y-2">
                  <Label htmlFor="from-email">From Email</Label>
                  <Input
                    id="from-email"
                    value={emailConfig.fromEmail}
                    onChange={(e) => setEmailConfig({ ...emailConfig, fromEmail: e.target.value })}
                    placeholder="noreply@path.secretsauce.fyi"
                    data-testid="email-from-input"
                  />
                  <p className="text-xs text-muted-foreground">Must use a verified Resend domain</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="from-name">From Name</Label>
                  <Input
                    id="from-name"
                    value={emailConfig.fromName}
                    onChange={(e) => setEmailConfig({ ...emailConfig, fromName: e.target.value })}
                    placeholder="Secret Sauce"
                    data-testid="email-from-name-input"
                  />
                  <p className="text-xs text-muted-foreground">Display name in email clients</p>
                </div>
              </div>

              <div className="max-w-sm space-y-2">
                <Label htmlFor="reply-to">Reply-To Email</Label>
                <Input
                  id="reply-to"
                  value={emailConfig.replyToEmail}
                  onChange={(e) => setEmailConfig({ ...emailConfig, replyToEmail: e.target.value })}
                  placeholder="hello@path.secretsauce.fyi"
                  data-testid="email-reply-to-input"
                />
                <p className="text-xs text-muted-foreground">Where replies from recipients go</p>
              </div>

              <Button onClick={handleSaveEmailConfig} disabled={savingEmail} data-testid="save-email-config-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingEmail ? 'Saving...' : 'Save Email Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PDF Tab */}
        <TabsContent value="pdf">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" /> PDF Configuration (MarkupGo)
              </CardTitle>
              <CardDescription>
                Configure PDF generation settings and MarkupGo API connection.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* MarkupGo API Key Status */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">MarkupGo API Key</p>
                  <p className="text-sm text-muted-foreground">Required for PDF generation</p>
                </div>
                <div className="flex items-center gap-2">
                  {pdfKeyInfo?.isSet ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <CheckCircle className="w-3 h-3 mr-1" /> ENV: {pdfKeyInfo.preview}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      <AlertCircle className="w-3 h-3 mr-1" /> Not Set
                    </Badge>
                  )}
                </div>
              </div>

              <div className="max-w-lg space-y-2">
                <Label htmlFor="markupgo-url">MarkupGo API URL</Label>
                <Input
                  id="markupgo-url"
                  value={pdfConfig.markupgoApiUrl}
                  onChange={(e) => setPdfConfig({ ...pdfConfig, markupgoApiUrl: e.target.value })}
                  placeholder="https://api.markupgo.com/api/v1"
                  data-testid="markupgo-url-input"
                />
              </div>

              <div>
                <Label className="mb-3 block">Default Page Margins</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
                  <div className="space-y-1">
                    <Label htmlFor="margin-top" className="text-xs text-muted-foreground">Top</Label>
                    <Input id="margin-top" value={pdfConfig.defaultMarginTop} onChange={(e) => setPdfConfig({ ...pdfConfig, defaultMarginTop: e.target.value })} placeholder="10mm" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="margin-bottom" className="text-xs text-muted-foreground">Bottom</Label>
                    <Input id="margin-bottom" value={pdfConfig.defaultMarginBottom} onChange={(e) => setPdfConfig({ ...pdfConfig, defaultMarginBottom: e.target.value })} placeholder="10mm" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="margin-left" className="text-xs text-muted-foreground">Left</Label>
                    <Input id="margin-left" value={pdfConfig.defaultMarginLeft} onChange={(e) => setPdfConfig({ ...pdfConfig, defaultMarginLeft: e.target.value })} placeholder="10mm" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="margin-right" className="text-xs text-muted-foreground">Right</Label>
                    <Input id="margin-right" value={pdfConfig.defaultMarginRight} onChange={(e) => setPdfConfig({ ...pdfConfig, defaultMarginRight: e.target.value })} placeholder="10mm" />
                  </div>
                </div>
              </div>

              <div className="max-w-xs space-y-2">
                <Label htmlFor="orientation">Default Orientation</Label>
                <Select value={pdfConfig.defaultOrientation} onValueChange={(v) => setPdfConfig({ ...pdfConfig, defaultOrientation: v })}>
                  <SelectTrigger id="orientation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="landscape">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Connection Test */}
              {markupGoStatus && (
                <div className={`p-4 rounded-lg ${markupGoStatus.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {markupGoStatus.success ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={markupGoStatus.success ? 'text-emerald-700' : 'text-red-700'}>
                      {markupGoStatus.success ? markupGoStatus.message : markupGoStatus.error}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4">
                <Button onClick={handleSavePdfConfig} disabled={savingPdf} data-testid="save-pdf-config-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {savingPdf ? 'Saving...' : 'Save PDF Settings'}
                </Button>
                <Button variant="outline" onClick={handleTestMarkupGo} disabled={testingMarkupGo}>
                  {testingMarkupGo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Test MarkupGo
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lunacal Tab */}
        <TabsContent value="lunacal">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" /> Lunacal Integration
              </CardTitle>
              <CardDescription>
                Configure the Lunacal.ai booking widget for facilitated sessions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">Enable Booking</p>
                  <p className="text-sm text-muted-foreground">
                    Allow participants to book facilitated sessions via Lunacal
                  </p>
                </div>
                <Switch
                  checked={lunacalSettings.enabled}
                  onCheckedChange={(checked) => setLunacalSettings({ ...lunacalSettings, enabled: checked })}
                  data-testid="lunacal-enabled-switch"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Booking URL</Label>
                  <Input
                    value={lunacalSettings.bookingUrl}
                    onChange={(e) => setLunacalSettings({ ...lunacalSettings, bookingUrl: e.target.value })}
                    className="mt-2"
                    placeholder="https://book.secretsauce.fyi/team/secretsauce/dicover-path"
                    data-testid="lunacal-url-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Your full Lunacal booking page URL
                  </p>
                </div>

                <div>
                  <Label>Calendar Link (calLink)</Label>
                  <Input
                    value={lunacalSettings.calLink}
                    onChange={(e) => setLunacalSettings({ ...lunacalSettings, calLink: e.target.value })}
                    className="mt-2"
                    placeholder="team/secretsauce/dicover-path"
                    data-testid="lunacal-callink-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    The calLink path used in the Lunacal embed code
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Session Name</Label>
                  <Input
                    value={lunacalSettings.sessionName}
                    onChange={(e) => setLunacalSettings({ ...lunacalSettings, sessionName: e.target.value })}
                    className="mt-2"
                    placeholder="Discover PATH"
                  />
                </div>

                <div>
                  <Label>Session Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={lunacalSettings.sessionDuration}
                    onChange={(e) => setLunacalSettings({ ...lunacalSettings, sessionDuration: parseInt(e.target.value) || 30 })}
                    className="mt-2"
                    min="15"
                    max="180"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">Forward Query Parameters</p>
                  <p className="text-sm text-muted-foreground">
                    Forward URL parameters (e.g. UTM tags) to the Lunacal booking
                  </p>
                </div>
                <Switch
                  checked={lunacalSettings.forwardQueryParams}
                  onCheckedChange={(checked) => setLunacalSettings({ ...lunacalSettings, forwardQueryParams: checked })}
                />
              </div>

              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Webhook URL</h4>
                <p className="text-xs text-muted-foreground mb-2">
                  Add this URL in your Lunacal event &rarr; Advanced &rarr; Webhooks:
                </p>
                <code className="block bg-card p-3 rounded text-sm break-all">
                  {window.location.origin}/api/webhooks/lunacal
                </code>
              </div>

              <div className="flex items-center gap-4">
                <Button onClick={handleSaveLunacalSettings} disabled={savingLunacal}>
                  <Save className="w-4 h-4 mr-2" />
                  {savingLunacal ? 'Saving...' : 'Save Lunacal Settings'}
                </Button>
                <a
                  href={lunacalSettings.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  Preview Booking Page
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* General Settings Tab */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" /> General Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">Assessment Auto-Save</p>
                  <p className="text-sm text-muted-foreground">
                    Automatically save assessment progress every response
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">
                    Send email notifications for key events
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">PDF Auto-Generation</p>
                  <p className="text-sm text-muted-foreground">
                    Automatically generate PDF when assessment completes
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Update API Key Dialog */}
      <Dialog open={showAddKey} onOpenChange={setShowAddKey}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isKeyConfigured(newKeyProvider) ? 'Update' : 'Add'} API Key
            </DialogTitle>
            <DialogDescription>
              Enter the API key for {API_KEY_PROVIDERS.find(p => p.value === newKeyProvider)?.label || newKeyProvider}
              {envKeys[newKeyProvider]?.isSet && (
                <span className="block mt-2 text-amber-600">
                  Note: An environment variable is already set for this provider. Database keys override environment variables.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Provider</Label>
              <Select value={newKeyProvider} onValueChange={setNewKeyProvider}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {API_KEY_PROVIDERS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>API Key</Label>
              <div className="relative mt-2">
                <Input
                  type={showKeyValue ? 'text' : 'password'}
                  value={newKeyValue}
                  onChange={(e) => setNewKeyValue(e.target.value)}
                  placeholder="Enter API key..."
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowKeyValue(!showKeyValue)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showKeyValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                The key will be encrypted before storage
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddKey(false);
              setNewKeyValue('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddApiKey} disabled={saving || !newKeyProvider || !newKeyValue}>
              {saving ? 'Saving...' : 'Save Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
